import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  HelpCircle, Plus, Edit, Trash2, Search, 
  X, Check, Eye, EyeOff, Layers
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

import API from '../../config/api';

const AdminFaqs = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    category: 'general',
    order: 0,
    is_active: true
  });

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/faqs?all=true`);
      setFaqs(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading FAQs:', error);
      toast.error('Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAdd = () => {
    setEditingFaq(null);
    setFormData({
      question: '',
      answer: '',
      category: 'general',
      order: faqs.length + 1,
      is_active: true
    });
    setShowModal(true);
  };

  const handleOpenEdit = (faq) => {
    setEditingFaq(faq);
    setFormData({
      question: faq.question || '',
      answer: faq.answer || '',
      category: faq.category || 'general',
      order: faq.order || 0,
      is_active: faq.is_active !== undefined ? faq.is_active : true
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.question.trim() || !formData.answer.trim()) {
      toast.error('Question and Answer are required');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        order: Number(formData.order) || 0
      };

      if (editingFaq) {
        await axios.put(`${API}/faqs/${editingFaq.id}`, payload, { withCredentials: true });
        toast.success('FAQ updated successfully');
      } else {
        await axios.post(`${API}/faqs`, payload, { withCredentials: true });
        toast.success('FAQ created successfully');
      }

      setShowModal(false);
      fetchFaqs();
    } catch (error) {
      console.error('Error saving FAQ:', error);
      toast.error(error.response?.data?.detail || 'Failed to save FAQ');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (faq) => {
    if (!window.confirm(`Are you sure you want to delete this FAQ?`)) return;

    try {
      await axios.delete(`${API}/faqs/${faq.id}`, { withCredentials: true });
      toast.success('FAQ deleted successfully');
      fetchFaqs();
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete FAQ');
    }
  };

  const filteredFaqs = faqs.filter(f =>
    f.question?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    f.answer?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    f.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      
      {/* Top Header */}
      <div className="bg-white rounded-2xl p-5 border border-[#1A1A1A]/[0.08] shadow-xs flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-xl font-bold text-gray-900 flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-[#D9711F]" />
            Site-wide &amp; Fallback FAQs
          </h1>
          <p className="text-xs text-gray-500">
            These FAQs show on the Homepage and automatically serve as fallbacks for any product without custom FAQs.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative min-w-[220px]">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search FAQs..."
              className="pl-9 text-xs"
            />
          </div>
          <Button
            onClick={handleOpenAdd}
            className="btn-saffron text-xs font-bold flex items-center gap-1.5 px-4 py-2 rounded-xl shadow-xs"
          >
            <Plus className="w-4 h-4" />
            Add FAQ
          </Button>
        </div>
      </div>

      {/* FAQs Table Card */}
      <div className="bg-white rounded-2xl border border-[#1A1A1A]/[0.08] shadow-xs overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="w-8 h-8 border-3 border-[#6E1B34] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredFaqs.length === 0 ? (
          <div className="text-center p-12">
            <HelpCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-sm font-semibold text-gray-700">No FAQs found</p>
            <p className="text-xs text-gray-400 mt-1">Create your first site-wide FAQ</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-gray-50 border-b border-gray-100 text-gray-500 font-bold uppercase tracking-wider">
                <tr>
                  <th className="py-3.5 px-4">Order</th>
                  <th className="py-3.5 px-4">Question &amp; Answer</th>
                  <th className="py-3.5 px-4">Category</th>
                  <th className="py-3.5 px-4 text-center">Status</th>
                  <th className="py-3.5 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 font-medium text-gray-800">
                {filteredFaqs.map((faq, idx) => (
                  <tr key={faq.id || idx} className="hover:bg-gray-50/70 transition-colors">
                    <td className="py-3.5 px-4 text-gray-400 font-mono align-top">
                      #{faq.order || idx + 1}
                    </td>
                    <td className="py-3.5 px-4 align-top max-w-xl">
                      <h4 className="font-bold text-gray-900 mb-1">{faq.question}</h4>
                      <p className="text-xs text-gray-600 line-clamp-2 leading-relaxed">{faq.answer}</p>
                    </td>
                    <td className="py-3.5 px-4 align-top">
                      <span className="bg-[#FBF4E7] text-[#4E1225] font-semibold px-2 py-0.5 rounded text-[11px] border border-[#E7D9BC]">
                        {faq.category || 'general'}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-center align-top">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        faq.is_active 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-gray-100 text-gray-500'
                      }`}>
                        {faq.is_active ? 'Active' : 'Hidden'}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-right align-top">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleOpenEdit(faq)}
                          className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-600 hover:text-[#6E1B34] transition-colors"
                          title="Edit FAQ"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(faq)}
                          className="p-1.5 hover:bg-red-50 rounded-lg text-red-500 hover:text-red-700 transition-colors"
                          title="Delete FAQ"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add / Edit FAQ Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl border border-gray-100 space-y-4 animate-scale-up">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100">
              <h2 className="font-heading text-base font-bold text-gray-900">
                {editingFaq ? 'Edit FAQ' : 'Add New FAQ'}
              </h2>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-xs font-bold text-gray-800">Question *</Label>
                <Input
                  value={formData.question}
                  onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                  placeholder="e.g. Has the product been energised before shipping?"
                  className="mt-1 text-xs font-medium"
                  required
                />
              </div>

              <div>
                <Label className="text-xs font-bold text-gray-800">Answer *</Label>
                <textarea
                  value={formData.answer}
                  onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                  placeholder="e.g. Yes — every item undergoes Prana Pratishtha by Himalayan pandits..."
                  rows={4}
                  className="w-full mt-1 border border-gray-200 rounded-xl p-2.5 text-xs text-[#2A1F16] focus:outline-none focus:border-[#D9711F] leading-relaxed"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-bold text-gray-800">Category</Label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full mt-1 border border-gray-200 rounded-xl px-3 py-2 text-xs bg-white focus:outline-none focus:border-[#D9711F]"
                  >
                    <option value="general">General</option>
                    <option value="products">Products</option>
                    <option value="rituals">Rituals &amp; Pujas</option>
                    <option value="shipping">Shipping &amp; Delivery</option>
                    <option value="payment">Payment &amp; COD</option>
                  </select>
                </div>

                <div>
                  <Label className="text-xs font-bold text-gray-800">Display Order</Label>
                  <Input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: e.target.value })}
                    placeholder="1"
                    className="mt-1 text-xs"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <div>
                  <p className="text-xs font-bold text-gray-900">Active Status</p>
                  <p className="text-[10px] text-gray-500">Show on homepage and fallback for products</p>
                </div>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 accent-[#6E1B34] rounded cursor-pointer"
                />
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowModal(false)}
                  className="text-xs font-semibold"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="btn-saffron text-xs font-bold px-5"
                >
                  {submitting ? 'Saving...' : editingFaq ? 'Update FAQ' : 'Create FAQ'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminFaqs;
